/**
 * AIReviewRequests — pupil's inbox of all AI Lesson Review requests.
 *
 * Each row shows status pill + focus + timing + amount, with a deep
 * link into the Passport once released.
 *
 * On mount, if the URL carries `?session_id=...` (Stripe success
 * redirect), poll `/checkout-status/{session_id}` once to flip the
 * row from awaiting_payment → awaiting_instructor_upload.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import {
  Sparkles, Clock, CheckCircle2, Upload, AlertCircle, ChevronLeft,
} from "lucide-react";
import { MEDIA_FEATURE_ENABLED } from "../lib/mediaFeatureFlag";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  awaiting_payment: { label: "Awaiting payment", Icon: Clock, tone: "amber" },
  awaiting_instructor_upload: { label: "Awaiting instructor upload", Icon: Upload, tone: "indigo" },
  processing: { label: "AI analysing", Icon: Clock, tone: "indigo" },
  completed_pending_instructor_preview: { label: "Awaiting instructor release", Icon: Clock, tone: "indigo" },
  released_to_pupil: { label: "Ready on Passport", Icon: CheckCircle2, tone: "emerald" },
  failed: { label: "Failed", Icon: AlertCircle, tone: "rose" },
  cancelled: { label: "Cancelled", Icon: AlertCircle, tone: "slate" },
};

const TONE = {
  amber: "bg-amber-500/15 border border-amber-500/30 text-amber-200",
  indigo: "bg-indigo-500/15 border border-indigo-500/30 text-indigo-200",
  emerald: "bg-emerald-500/15 border border-emerald-500/30 text-emerald-200",
  rose: "bg-rose-500/15 border border-rose-500/30 text-rose-200",
  slate: "bg-slate-700/40 border border-slate-700 text-slate-300",
};

const FOCUS_LABEL = {
  roundabouts: "Roundabouts",
  junctions: "Junctions",
  speed_control: "Speed control",
  mirrors: "Mirrors",
  general_driving: "General driving",
  other: "Other",
};

export default function AIReviewRequests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();

  const fetchItems = async () => {
    if (!MEDIA_FEATURE_ENABLED) {
      // Media quarantine — page is route-quarantined too; this is
      // the defensive doctrine pin.
      setItems([]);
      setLoading(false);
      return;
    }
    try {
      const r = await axios.get(
        `${API}/api/lesson-review-requests/my-requests`,
        { withCredentials: true }
      );
      setItems(r.data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const sid = params.get("session_id");
    (async () => {
      if (sid) {
        try {
          await axios.get(
            `${API}/api/lesson-review-requests/checkout-status/${sid}`,
            { withCredentials: true }
          );
        } catch {
          // Failure is non-fatal — user can re-poll by reopening.
        }
      }
      await fetchItems();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-950 text-white">
      <header className="px-4 py-4 border-b border-slate-800 flex items-center gap-3">
        <Link to="/learn" className="p-2 rounded-lg hover:bg-white/5">
          <ChevronLeft className="w-5 h-5 text-slate-400" />
        </Link>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-widest font-extrabold text-indigo-300">
            AI Lesson Reviews
          </p>
          <h1 className="text-lg font-extrabold">Your requests</h1>
        </div>
      </header>
      <div className="px-4 py-4 max-w-lg mx-auto space-y-3">
        {loading ? (
          <p data-testid="ai-rr-loading" className="text-sm text-slate-400">Loading…</p>
        ) : items.length === 0 ? (
          <div
            data-testid="ai-rr-empty"
            className="rounded-2xl border border-slate-800 bg-slate-900 p-6 text-center"
          >
            <Sparkles className="w-6 h-6 text-indigo-300 mx-auto" />
            <p className="text-sm text-slate-300 mt-2">
              You haven't requested an AI review yet.
            </p>
            <Link
              to="/learn"
              className="mt-3 inline-block text-[12px] text-indigo-300 hover:underline font-extrabold"
            >
              Back to Learn
            </Link>
          </div>
        ) : (
          items.map((it) => {
            const tone = STATUS_LABELS[it.request_status] || STATUS_LABELS.awaiting_payment;
            const Icon = tone.Icon;
            return (
              <div
                key={it.id}
                data-testid={`ai-rr-row-${it.id}`}
                className="rounded-2xl border border-slate-800 bg-slate-900 p-4"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-extrabold text-white">
                      {FOCUS_LABEL[it.focus_area] || it.focus_area}
                      {it.focus_area === "other" && it.focus_other ? ` — ${it.focus_other}` : ""}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      {it.time_range?.preset === "custom"
                        ? `${Math.floor((it.time_range.start_seconds || 0) / 60)}:${String(Math.floor((it.time_range.start_seconds || 0) % 60)).padStart(2, "0")} → ${Math.floor((it.time_range.end_seconds || 0) / 60)}:${String(Math.floor((it.time_range.end_seconds || 0) % 60)).padStart(2, "0")}`
                        : it.time_range?.preset === "start" ? "Start of lesson"
                        : it.time_range?.preset === "middle" ? "Middle section"
                        : it.time_range?.preset === "end" ? "End of lesson"
                        : "—"}
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      <span
                        className={`inline-flex items-center gap-1.5 text-[10px] font-extrabold rounded-full px-2 py-0.5 ${TONE[tone.tone]}`}
                      >
                        <Icon className="w-3 h-3" /> {tone.label}
                      </span>
                      <span className="text-[10px] text-slate-500">
                        £{((it.amount_pence || 0) / 100).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  {it.request_status === "released_to_pupil" && (
                    <Link
                      to={`/passport?ai_review=${it.id}`}
                      className="text-[11px] font-extrabold text-emerald-300 hover:underline"
                    >
                      View →
                    </Link>
                  )}
                </div>
                {it.instructor_note && it.request_status === "released_to_pupil" && (
                  <div className="mt-3 rounded-lg bg-slate-800/60 border border-slate-800 p-3">
                    <p className="text-[10px] uppercase tracking-widest text-slate-500 font-extrabold mb-1">
                      Note from your instructor
                    </p>
                    <p className="text-[12px] text-slate-200">{it.instructor_note}</p>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
